import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Divider,
  Paper,
  Table,
  TableBody,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography
} from '@mui/material';
import { Fragment, useState } from 'react';

import BSTableBodyCell from '../table/components/BSTableBodyCell';
import BSTableHeaderCell from '../table/components/BSTableHeaderCell';

const OverlayScoreboard = ({ game }) => {
  const [open, setOpen] = useState(false);
  const [selectedEnd, setSelectedEnd] = useState(0);
  const [teamAScore, setTeamAScore] = useState(0);
  const [teamBScore, setTeamBScore] = useState(0);

  const currentSet = game.sets.find((set) => set.id === game.currentSet);
  const currentScores = currentSet.scores;

  const teamA = game.teams.teamA;
  const teamB = game.teams.teamB;
  const configuredSets =
    Number(game.gameFormat?.sets) || (Array.isArray(game.sets) ? game.sets.length : 0);
  const showSetTotals = configuredSets > 1 || Boolean(game.totalsOnly);
  const MAX_ENDS_PER_COLUMN = 12;

  const createScoreboardArray = (set) => {
    const scoreboard = [];
    const setScores = set.scores;

    for (let i = 0; i < setScores.length; i++) {
      const teamAScore = setScores[i].teamScores[teamA.id] ?? 0;
      const teamBScore = setScores[i].teamScores[teamB.id] ?? 0;
      const multiplierTeamA = setScores[i].multiplierActive
        ? setScores[i].multiplierActive[teamA.id]
        : false;
      const multiplierTeamB = setScores[i].multiplierActive
        ? setScores[i].multiplierActive[teamB.id]
        : false;

      const teamATotal = setScores
        .slice(0, i + 1)
        .reduce((acc, score) => acc + (score.teamScores[teamA.id] ?? 0), 0);
      const teamBTotal = setScores
        .slice(0, i + 1)
        .reduce((acc, score) => acc + (score.teamScores[teamB.id] ?? 0), 0);
      const End = i + 1;
      scoreboard.push({
        teamAScore,
        teamATotal,
        teamBScore,
        teamBTotal,
        End,
        multiplierTeamA,
        multiplierTeamB
      });
    }
    scoreboard.sort((a, b) => b.End - a.End);
    return scoreboard;
  };

  const createPlayoffScoreboard = (playoff) => {
    const scoreboard = [];
    const playoffEnds = playoff.ends;

    if (playoffEnds) {
      for (let i = 0; i < playoffEnds.length; i++) {
        const teamAScore = playoffEnds[i].winningTeam === teamA.id ? 1 : 0;
        const teamBScore = playoffEnds[i].winningTeam === teamB.id ? 1 : 0;

        const teamATotal = playoffEnds
          .slice(0, i + 1)
          .reduce((acc, score) => acc + (score.winningTeam === teamA.id ? 1 : 0), 0);
        const teamBTotal = playoffEnds
          .slice(0, i + 1)
          .reduce((acc, score) => acc + (score.winningTeam === teamB.id ? 1 : 0), 0);

        const End = i + 1;

        scoreboard.push({ teamAScore, teamATotal, teamBScore, teamBTotal, End });
      }

      scoreboard.sort((a, b) => b.End - a.End);
    }

    if (playoff.teamScores) {
      const teamAScore = playoff.teamScores[teamA.id];
      const teamBScore = playoff.teamScores[teamB.id];
      scoreboard.push({
        teamAScore,
        teamATotal: teamAScore,
        teamBScore,
        teamBTotal: teamBScore,
        End: 1
      });
    }

    return scoreboard;
  };

  const handleEndEdit = (end) => {
    setSelectedEnd(end);
    const currentEnd = currentScores.find((score) => score.game_end === end);
    setTeamAScore(currentEnd.teamScores[teamA.id] ?? 0);
    setTeamBScore(currentEnd.teamScores[teamB.id] ?? 0);
    setOpen(true);
  };

  const handleEndSave = () => {
    console.log('Save End');
  };

  const handleClose = () => {
    setOpen(false);
  };

  const row = (
    teamAScore,
    teamATotal,
    teamBSocre,
    teamBTotal,
    End,
    multiplierTeamA,
    multiplierTeamB,
    playoff
  ) => {
    return (
      <TableRow>
        <BSTableBodyCell align="center">
          <Typography variant="caption" color={multiplierTeamA ? 'primary' : 'text'}>
            {teamAScore} {multiplierTeamA && 'PP'}
          </Typography>
        </BSTableBodyCell>
        <BSTableBodyCell align="center">
          <Typography variant="caption">{teamATotal}</Typography>
        </BSTableBodyCell>
        <BSTableBodyCell align="center">
          <Typography
            sx={{ cursor: 'pointer' }}
            variant="caption"
            color={playoff ? 'warning.main' : 'primary'}
            fontWeight={'bold'}
            onClick={() => handleEndEdit(End)}>
            {End}
          </Typography>
        </BSTableBodyCell>
        <BSTableBodyCell align="center">
          <Typography variant="caption" color={multiplierTeamB ? 'primary' : 'text'}>
            {teamBSocre} {multiplierTeamB && 'PP'}
          </Typography>
        </BSTableBodyCell>
        <BSTableBodyCell align="center">
          <Typography variant="caption">{teamBTotal}</Typography>
        </BSTableBodyCell>
      </TableRow>
    );
  };

  const chunkScoreboard = (items, size) => {
    const chunks = [];

    for (let index = 0; index < items.length; index += size) {
      chunks.push(items.slice(index, index + size));
    }

    return chunks;
  };

  const renderScoreboardTable = (items, playoff = false) => {
    return (
      <TableContainer>
        <Table size="small">
          <TableHead>
            <TableRow>
              <BSTableHeaderCell align="center">
                <Typography variant="body1">Score</Typography>
              </BSTableHeaderCell>
              <BSTableHeaderCell align="center">
                <Typography variant="body1">Total</Typography>
              </BSTableHeaderCell>
              <BSTableHeaderCell align="center">
                <Typography
                  variant="body1"
                  color={playoff ? 'warning.main' : 'primary'}
                  fontWeight={'bold'}>
                  End
                </Typography>
              </BSTableHeaderCell>
              <BSTableHeaderCell align="center">
                <Typography variant="body1">Score</Typography>
              </BSTableHeaderCell>
              <BSTableHeaderCell align="center">
                <Typography variant="body1">Total</Typography>
              </BSTableHeaderCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {items.map((item) => (
              <Fragment key={item.End}>
                {row(
                  item.teamAScore,
                  item.teamATotal,
                  item.teamBScore,
                  item.teamBTotal,
                  item.End,
                  item.multiplierTeamA,
                  item.multiplierTeamB,
                  playoff
                )}
              </Fragment>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
    );
  };

  const endsScoreboard = () => {
    return (
      <Box
        display={'flex'}
        flexWrap={'wrap'}
        gap={2}
        justifyContent={'center'}
        alignItems={'flex-start'}>
        {game.sets
          .slice()
          .reverse()
          .map((set, index) =>
            chunkScoreboard(createScoreboardArray(set), MAX_ENDS_PER_COLUMN).map(
              (scoreChunk, chunkIndex) => (
                <Paper
                  sx={{ p: 2, borderRadius: 5, my: 2, minWidth: 280 }}
                  key={`${set.id || index}-${chunkIndex}`}>
                  {chunkIndex === 0 && game.sets.length > 1 && !set.totalSetScore && (
                    <Typography variant="h5" textAlign={'center'}>
                      {game.gameFormat.setAsSkin ? `Skin ${set.setNumber}` : `Set ${set.setNumber}`}
                    </Typography>
                  )}

                  {chunkIndex === 0 && showSetTotals && set.totalSetScore && (
                    <>
                      <Box display={'flex'} justifyContent={'space-evenly'} alignItems={'center'}>
                        <Typography variant="body1">Total</Typography>
                        <Typography variant="body1" color={'info.main'} fontWeight={'bold'}>
                          {game.totalsOnly
                            ? 'Totals'
                            : game.gameFormat.setAsSkin
                              ? `Skin ${set.setNumber}`
                              : `Set ${set.setNumber}`}
                        </Typography>
                        <Typography variant="body1">Total</Typography>
                      </Box>

                      <Box display={'flex'} justifyContent={'space-evenly'} alignItems={'center'}>
                        <Typography variant="body1" color={'info.main'} fontSize={'1.5em'}>
                          {set.totalSetScore[teamA.id] ?? 0}
                        </Typography>
                        <Typography variant="body1">
                          {set.finalScoreOnly ? 'Final Score' : ''}
                        </Typography>
                        <Typography variant="body1" color={'info.main'} fontSize={'1.5em'}>
                          {set.totalSetScore[teamB.id] ?? 0}
                        </Typography>
                      </Box>
                    </>
                  )}

                  {chunkIndex === 0 && <Divider color={'info.main'} sx={{ my: 2 }} />}

                  {!set.finalScoreOnly ? renderScoreboardTable(scoreChunk) : null}
                </Paper>
              )
            )
          )}
      </Box>
    );
  };

  const finalTotals = () => {
    let teamATotal = 0;
    let teamBTotal = 0;

    game.sets.forEach((set) => {
      if (set.totalSetScore) {
        teamATotal += set.totalSetScore[teamA.id] || 0;
        teamBTotal += set.totalSetScore[teamB.id] || 0;
      }
    });

    return (
      <>
        <Box display={'flex'} justifyContent={'space-evenly'} alignItems={'center'}>
          <Typography variant="body1">Total</Typography>
          <Typography variant="body1" color={'success.main'} fontWeight={'bold'}>
            Game Final Total
          </Typography>
          <Typography variant="body1">Total</Typography>
        </Box>

        <Box display={'flex'} justifyContent={'space-evenly'} alignItems={'center'}>
          <Typography variant="body1" color={'success.main'} fontSize={'1.5em'}>
            {teamATotal || 0} ({game.teamTotals[teamA.id]})
          </Typography>
          <Typography variant="body1">{'Final Score'}</Typography>
          <Typography variant="body1" color={'success.main'} fontSize={'1.5em'}>
            {teamBTotal || 0} ({game.teamTotals[teamB.id]})
          </Typography>
        </Box>
      </>
    );
  };

  return (
    <>
      <Box display={'flex'} mt={3} flexDirection={'column'} alignItems={'center'} width="100%">
        <Typography variant="h4">Scorecard</Typography>
      </Box>

      {finalTotals()}

      {game.playoff && (
        <Paper sx={{ m: 2, p: 2, borderRadius: 5 }}>
          <Typography variant="h5" textAlign={'center'}>
            Tiebreaker
          </Typography>

          {renderScoreboardTable(createPlayoffScoreboard(game.playoff), true)}
        </Paper>
      )}

      {endsScoreboard()}

      {selectedEnd > 0 && (
        <Dialog open={open} onClose={handleClose}>
          <DialogTitle>Edit End {selectedEnd}</DialogTitle>
          <DialogContent>
            <DialogContentText>Team A</DialogContentText>
            <TextField
              autoFocus
              margin="dense"
              id="teamA"
              label="Team A Score"
              type="number"
              fullWidth
              value={teamAScore}
              onChange={(e) => setTeamAScore(e.target.value)}
            />
            <DialogContentText>Team B</DialogContentText>
            <TextField
              margin="dense"
              id="teamB"
              label="Team B Score"
              type="number"
              fullWidth
              value={teamBScore}
              onChange={(e) => setTeamBScore(e.target.value)}
            />
          </DialogContent>
          <DialogActions>
            <Button onClick={handleClose}>Cancel</Button>
            <Button onClick={handleEndSave}>Save</Button>
          </DialogActions>
        </Dialog>
      )}
    </>
  );
};

export default OverlayScoreboard;
